import type {AppConfig} from '@/hooks/app-config-helpers'
import type {AttributeDefinition} from '@/types/attribute-definition'
import {IamCelCompiler} from './ruleengine/cel/IamCelCompiler'
import {IamCelSuggestionEngine} from './ruleengine/cel/IamCelSuggestionEngine'
import {IamRuleExpressionSchema} from './ruleengine/cel/IamRuleExpressionSchema'
import type {EnumValueProvider} from './ruleengine/api/EnumValueProvider'
import {
    certificationAccessSchemaBuilder,
    fieldDefinitionIsDynamic,
    fieldDefinitionIsScope,
    iamMultiRootSchemaBuilder,
    profileSchema,
    type ExpressionFieldRegistryBuilder,
    type FieldDefinition,
    type FieldNameProvider,
} from './ruleengine/api/ExpressionFieldRegistry'

/**
 * Construct the in-browser CEL suggestion engine for the supplied
 * {@link AppConfig} snapshot. The engine consumes the same registry shape the
 * backend uses, so completions are byte-identical across the wire — except
 * computed in the browser, without a `/suggest` roundtrip.
 *
 * The factory is pure: same `AppConfig` snapshot in → same engine out. Wrap
 * with `useMemo(() => buildCelEngine(config), [config])` (or
 * {@link useCelEngine}) at the call site to avoid rebuilding per keystroke.
 */
export function buildCelEngine(config: AppConfig): IamCelSuggestionEngine {
    return buildSuggestionEngineFromBuilder(iamMultiRootSchemaBuilder(), config)
}

/**
 * Same as {@link buildCelEngine} but for the profile schema (restriction
 * rules — single `profile` root, no identity/party/etc). Enums still come
 * from AppConfig so risk-level codes stay in sync.
 */
export function buildProfileCelEngine(config: AppConfig): IamCelSuggestionEngine {
    const registry = profileSchema()
    const schema = new IamRuleExpressionSchema(registry)
    const compiler = new IamCelCompiler(schema)
    const enums = enumProviderFromAppConfig(config)
    return new IamCelSuggestionEngine(compiler, enums)
}

/**
 * Same as {@link buildProfileCelEngine} but for the certification-policy scope
 * schema (epic 924 — `access` root over the certified AccessProfileAssignment
 * spine). Enums come from AppConfig so risk-level / environment / system-type
 * codes stay in sync; `riskLevel` carries the ranked-operator widening via the
 * AppConfig-derived ranked type-table set.
 */
export function buildCertificationCelEngine(config: AppConfig): IamCelSuggestionEngine {
    return buildSuggestionEngineFromBuilder(certificationAccessSchemaBuilder(), config)
}

function buildSuggestionEngineFromBuilder(
    b: ExpressionFieldRegistryBuilder,
    config: AppConfig,
): IamCelSuggestionEngine {
    b.rankedEnumTypeNames(rankedTypeTableNames(config))
    attachAllDynamicProviders(b, config)
    const schema = new IamRuleExpressionSchema(b.build())
    const compiler = new IamCelCompiler(schema)
    const enums = enumProviderFromAppConfig(config)
    return new IamCelSuggestionEngine(compiler, enums)
}

/**
 * Issue 781 — derive the ranked type-table set from the AppConfig snapshot.
 * A type-table counts as ranked when any active row carries `ranked = true`;
 * the suggester then folds the flag onto every FieldDefinition whose
 * `enumTypeName` matches, widening the operator pool to include `<`, `<=`,
 * `>`, `>=` for those fields. Static enums (no `ranked` column) are excluded
 * because their `TypeValue.ranked` is always false.
 */
function rankedTypeTableNames(config: AppConfig): ReadonlySet<string> {
    const byName = config.typeValuesByName ?? {}
    const names = new Set<string>()
    for (const [tableName, values] of Object.entries(byName)) {
        if (values.some(v => v.ranked)) {
            names.add(tableName)
        }
    }
    return names
}

function attachAttributeProvider(
    builder: ExpressionFieldRegistryBuilder,
    path: string,
    defs: AttributeDefinition[],
): void {
    if (!defs?.length) return
    const active = defs.filter(d => d.active).map(d => ({name: d.name, displayName: d.displayName || d.name}))
    const provider: FieldNameProvider = {
        getFieldNames(prefix: string) {
            return active.filter(a => a.name.startsWith(prefix))
        },
    }
    builder.fieldNameProvider(path, provider)
}

/**
 * Walk the schema currently held by the builder. For every DYNAMIC field
 * that carries a `dynamicSource`, look up the AppConfig field of that
 * name and — when it contains AttributeDefinition rows — register a
 * field-name provider at the field's full dotted path. The schema is the
 * single source of truth: no factory-side path constants, no per-scope
 * call list. Issue 936 follow-up to eliminate the brittle hand-wiring
 * the HQG flagged on bf8693b62 / 2fd8ffc47e.
 *
 * Scope fields are looked up by NAME (the same scope reached via
 * different roots emits the provider at each reachable full path).
 * Walks roots and collections; ignores cycles by tracking visited
 * full paths.
 */
export function attachAllDynamicProviders(
    builder: ExpressionFieldRegistryBuilder,
    config: AppConfig,
): void {
    const snapshot = builder.build()
    const configRecord = config as unknown as Record<string, AttributeDefinition[] | undefined>
    const visitedPaths = new Set<string>()

    function walk(prefix: string, fields: ReadonlyMap<string, FieldDefinition>): void {
        for (const [name, def] of fields) {
            const fullPath = `${prefix}.${name}`
            if (fieldDefinitionIsDynamic(def) && def.dynamicSource !== null) {
                const defs = configRecord[def.dynamicSource]
                if (defs && defs.length > 0) {
                    attachAttributeProvider(builder, fullPath, defs)
                }
            } else if (fieldDefinitionIsScope(def)) {
                if (visitedPaths.has(fullPath)) continue
                visitedPaths.add(fullPath)
                const scopeFields = snapshot.scopeFields(name)
                if (scopeFields.size > 0) walk(fullPath, scopeFields)
            }
        }
    }

    for (const rootName of snapshot.rootNames()) {
        walk(rootName, snapshot.rootFields(rootName))
    }
}

function enumProviderFromAppConfig(config: AppConfig): EnumValueProvider {
    const byName = config.typeValuesByName ?? {}
    return {
        activeCodes(name: string): readonly string[] {
            const values = byName[name]
            if (!values) return []
            return values.map(v => v.code)
        },
    }
}
